import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Circle as CircleIcon,
  PowerSettingsNew as ConnectIcon,
  LinkOff as DisconnectIcon,
} from '@mui/icons-material';
import { ibkrService } from '../../services/ibkrService';

interface StatusData {
  connected: boolean;
  account?: {
    nlv?: number;
    net_liquidation?: number;
    buying_power?: number;
    cash?: number;
    total_cash?: number;
    daily_pnl?: number;
    daily_pnl_pct?: number;
    unrealized_pnl?: number;
    realized_pnl?: number;
  };
  auto_trading?: boolean;
  mode?: string;
  symbols?: string[];
  analysis_interval_min?: number;
  trades_today?: number;
  positions_count?: number;
  history_count?: number;
}

interface Position {
  id: string;
  symbol: string;
  strategy: string;
  pnl: number;
  dte: number;
  status: string;
}

interface Trade {
  date: string;
  symbol: string;
  strategy: string;
  pnl: number;
  status: string;
}

const IBKRDashboard: React.FC = () => {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [recentTrades, setRecentTrades] = useState<Trade[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      const [statusData, posData, histData] = await Promise.all([
        ibkrService.getStatus(),
        ibkrService.getPositions().catch(() => []),
        ibkrService.getHistory().catch(() => []),
      ]);
      setStatus(statusData);
      const rawPos = Array.isArray(posData) ? posData : (posData?.positions || []);
      setPositions(rawPos.map((p: any) => ({
        id: p.id ?? '',
        symbol: p.symbol ?? '',
        strategy: p.strategy_name ?? p.strategy ?? '',
        pnl: p.current_pnl ?? p.pnl ?? 0,
        dte: typeof p.dte === 'number' ? p.dte : 0,
        status: (p.current_pnl ?? 0) > 0 ? 'profit' : (p.current_pnl ?? 0) < 0 ? 'loss' : 'breakeven',
      })));
      setRecentTrades((histData?.trades || []).slice(0, 5));
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleConnect = async () => {
    setActionLoading(true);
    try {
      await ibkrService.connect();
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisconnect = async () => {
    setActionLoading(true);
    try {
      await ibkrService.disconnect();
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAutoToggle = async () => {
    setActionLoading(true);
    try {
      if (status?.auto_trading) {
        await ibkrService.stopAuto();
      } else {
        await ibkrService.startAuto();
      }
      await fetchData();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (val?: number) =>
    val != null ? `$${val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '--';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const connected = status?.connected ?? false;
  const account = status?.account;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        IBKR Dashboard
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Connection Status */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircleIcon sx={{ fontSize: 14, color: connected ? 'success.main' : 'error.main' }} />
          <Typography fontWeight={600}>
            IBKR Paper Account
          </Typography>
          <Chip
            label={connected ? 'Verbunden' : 'Getrennt'}
            color={connected ? 'success' : 'error'}
            size="small"
            variant="outlined"
          />
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={connected ? <DisconnectIcon /> : <ConnectIcon />}
          onClick={connected ? handleDisconnect : handleConnect}
          disabled={actionLoading}
          color={connected ? 'error' : 'primary'}
        >
          {connected ? 'Trennen' : 'Verbinden'}
        </Button>
      </Paper>

      {/* Account Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">NLV</Typography>
          <Typography variant="h6" fontWeight={700}>{formatCurrency(account?.net_liquidation ?? account?.nlv)}</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Kaufkraft</Typography>
          <Typography variant="h6" fontWeight={700}>{formatCurrency(account?.buying_power)}</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Bargeld</Typography>
          <Typography variant="h6" fontWeight={700}>{formatCurrency(account?.total_cash ?? account?.cash)}</Typography>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">Unrealisierter P&L</Typography>
          <Typography
            variant="h6"
            fontWeight={700}
            color={
              (account?.daily_pnl ?? 0) >= 0 ? 'success.main' : 'error.main'
            }
          >
            {formatCurrency(account?.unrealized_pnl ?? account?.daily_pnl ?? 0)}
            {account?.daily_pnl_pct != null && ` (${(account.daily_pnl_pct ?? 0) > 0 ? '+' : ''}${(account.daily_pnl_pct ?? 0).toFixed(1)}%)`}
          </Typography>
        </Paper>
      </Box>

      {/* Markt-Status */}
      {(() => {
        const now = new Date();
        const utcDay = now.getUTCDay();
        const utcHour = now.getUTCHours();
        const utcMin = now.getUTCMinutes();
        const utcTime = utcHour + utcMin / 60;
        // NYSE: Mo-Fr 14:30-21:00 UTC (regulaer), Extended: 09:00-02:00
        const isWeekend = utcDay === 0 || utcDay === 6;
        const isRegular = !isWeekend && utcTime >= 13.5 && utcTime < 20;
        const isExtended = !isWeekend && ((utcTime >= 9 && utcTime < 13.5) || (utcTime >= 20 && utcTime < 24));
        const label = isRegular ? 'Markt geoeffnet' : isExtended ? 'Extended Hours' : isWeekend ? 'Wochenende — Markt geschlossen' : 'Markt geschlossen';
        const color = isRegular ? 'success' : isExtended ? 'warning' : 'default';
        return (
          <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 1, bgcolor: isRegular ? '#f0fdf4' : isExtended ? '#fffbeb' : '#f5f5f5' }}>
            <Chip label={label} color={color as any} size="small" />
            <Typography variant="body2" color="text.secondary">
              {isRegular ? 'NYSE/NASDAQ 15:30–22:00 Uhr' : isExtended ? 'Eingeschraenkter Handel' : isWeekend ? 'Naechste Oeffnung: Montag 15:30 Uhr' : 'Naechste Oeffnung: 15:30 Uhr'}
            </Typography>
          </Paper>
        );
      })()}

      {/* Auto-Trading Toggle */}
      <Paper sx={{
        p: 2.5, mb: 2,
        border: (status?.auto_trading) ? '2px solid #22c55e' : '1px solid #ddd',
        bgcolor: (status?.auto_trading) ? '#f0fdf4' : 'background.paper',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography fontWeight={700} fontSize={18}>
              {status?.auto_trading ? '🟢 Auto-Trading aktiv' : '⚪ Auto-Trading inaktiv'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {status?.auto_trading
                ? `Analysiert alle ${status?.analysis_interval_min ?? 60} Minuten und handelt automatisch.`
                : 'Der MarktMeisterIB analysiert und handelt automatisch wenn aktiviert.'}
            </Typography>
          </Box>
          <Button
            variant={status?.auto_trading ? 'outlined' : 'contained'}
            color={status?.auto_trading ? 'error' : 'success'}
            onClick={handleAutoToggle}
            disabled={actionLoading || !connected}
            size="large"
            sx={{ minWidth: 160, fontWeight: 700 }}
          >
            {actionLoading ? 'Bitte warten...' : status?.auto_trading ? 'Stoppen' : 'Auto-Trading starten'}
          </Button>
        </Box>
        {status?.auto_trading && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ px: 1, py: 0.25, bgcolor: 'rgba(37, 99, 235, 0.14)', borderRadius: 1, fontWeight: 600 }}>
              Symbole: {(status?.symbols ?? []).join(', ')}
            </Typography>
            <Typography variant="caption" sx={{ px: 1, py: 0.25, bgcolor: 'rgba(37, 99, 235, 0.14)', borderRadius: 1, fontWeight: 600 }}>
              Max Risiko: {(((status as any)?.max_risk_pct ?? 0.05) * 100).toFixed(0)}%
            </Typography>
            <Typography variant="caption" sx={{ px: 1, py: 0.25, bgcolor: 'rgba(37, 99, 235, 0.14)', borderRadius: 1, fontWeight: 600 }}>
              Max Positionen: {(status as any)?.max_positions ?? 3}
            </Typography>
            <Typography variant="caption" sx={{ px: 1, py: 0.25, bgcolor: 'rgba(37, 99, 235, 0.14)', borderRadius: 1, fontWeight: 600 }}>
              Trades heute: {status?.trades_today ?? 0} / 5
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Active Positions */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography fontWeight={600} gutterBottom>
          Aktive Positionen ({positions.length})
        </Typography>
        {positions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Keine offenen Positionen</Typography>
        ) : (
          positions.map((pos) => (
            <Box
              key={pos.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {pos.symbol} {pos.strategy}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={pos.pnl >= 0 ? 'success.main' : 'error.main'}
                >
                  {pos.pnl >= 0 ? '+' : ''}{formatCurrency(pos.pnl)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {pos.dte} DTE
                </Typography>
                <CircleIcon
                  sx={{
                    fontSize: 10,
                    color: pos.status === 'profit' ? 'success.main' : pos.status === 'loss' ? 'error.main' : 'warning.main',
                  }}
                />
              </Box>
            </Box>
          ))
        )}
      </Paper>

      {/* Recent Trades */}
      <Paper sx={{ p: 2 }}>
        <Typography fontWeight={600} gutterBottom>
          Letzte Trades
        </Typography>
        {recentTrades.length === 0 ? (
          <Typography variant="body2" color="text.secondary">Noch keine Trades</Typography>
        ) : (
          recentTrades.map((trade, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                py: 1,
                borderBottom: 1,
                borderColor: 'divider',
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary">{trade.date}</Typography>
                <Typography variant="body2" fontWeight={600}>{trade.symbol} {trade.strategy}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label={trade.status} size="small" variant="outlined" />
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={trade.pnl >= 0 ? 'success.main' : 'error.main'}
                >
                  {trade.pnl >= 0 ? '+' : ''}{formatCurrency(trade.pnl)}
                </Typography>
              </Box>
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default IBKRDashboard;
